import { Box, Button, Card, CardHeader, Container, IconButton, Stack, Tab, Tabs, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { RentalOrganizationSettings } from "src/models";
import { RENTAL_TRAILER_TYPES } from "src/constants";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";

import PageHeader from "src/components/page-header";
import useModelSavingData from "src/hooks/use-model-saving-data";
import { usePrevious } from "src/hooks/use-previous";
import Iconify from "src/components/iconify/iconify";
import SimpleDialog from "src/components/simple-dialog";
import DisplayErrors from "src/components/display-errors";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";
import { RentalFileField } from "../trailers/rental-file-field";
import AgreementTemplateEditor from "./agreement-template-editor";

type PageModel = {
  rentalOrganizationSettings?: RentalOrganizationSettings,
}

const NAMED_PREFIX = "named:";

function agreementTabCaption(selectedType: string, isNamed: boolean) {
  if (isNamed) {
    return "An agreement you can pick when sending. Anything left empty falls back to the Default agreement.";
  }
  if (selectedType) {
    return "This agreement is used for trailers of this type. Anything left empty falls back to the Default agreement.";
  }
  return "The Default agreement is used for every trailer type without its own agreement.";
}

export default function Page() {
  const settings = useSettingsContext();

  const dataLoader = useCallback(() => ({
    rentalOrganizationSettings: axios.get(endpoints.rentalOrganizationSettings.current).then(response => response.data),
  }), []);

  const {
    data,
    updateData,
    error,
    loading,
  } = useModelData<PageModel>(dataLoader);

  const previousRentalOrganizationSettings = usePrevious(data?.rentalOrganizationSettings)

  const rentalOrganizationSettingsModel = useModelSavingData<RentalOrganizationSettings>(
    endpoints.rentalOrganizationSettings.update,
    endpoints.rentalOrganizationSettings.update
  );

  useEffect(() => {
    if (data?.rentalOrganizationSettings && data.rentalOrganizationSettings !== previousRentalOrganizationSettings) {
      rentalOrganizationSettingsModel.set(data.rentalOrganizationSettings)
    }
  }, [previousRentalOrganizationSettings, data?.rentalOrganizationSettings, rentalOrganizationSettingsModel]);

  // "agreements" | "insurance"
  const [documentTab, setDocumentTab] = useState("agreements");
  // '' = the default agreement, a RENTAL_TRAILER_TYPES value, or "named:<name>"
  const [selectedType, setSelectedType] = useState('');
  const [newAgreementOpen, setNewAgreementOpen] = useState(false);
  const [newAgreementName, setNewAgreementName] = useState('');

  const namedAgreements = useMemo(
    () => rentalOrganizationSettingsModel.model?.namedAgreements || [],
    [rentalOrganizationSettingsModel.model?.namedAgreements]
  );
  const selectedNamedAgreement = selectedType.startsWith(NAMED_PREFIX)
    ? namedAgreements.find((entry) => entry.name === selectedType.substring(NAMED_PREFIX.length))
    : undefined;
  const selectedOverride = !selectedType.startsWith(NAMED_PREFIX)
    ? rentalOrganizationSettingsModel.model?.typeAgreements
      ?.find((entry) => entry.trailerType === selectedType)
    : undefined;

  let agreementTemplate = rentalOrganizationSettingsModel.model?.agreementTemplate || "";
  let agreementFile = rentalOrganizationSettingsModel.model?.agreementFile || "";
  if (selectedNamedAgreement) {
    agreementTemplate = selectedNamedAgreement.agreementTemplate || "";
    agreementFile = selectedNamedAgreement.agreementFile || "";
  } else if (selectedType) {
    agreementTemplate = selectedOverride?.agreementTemplate || "";
    agreementFile = selectedOverride?.agreementFile || "";
  }

  const updateAgreement = useCallback((patch: { agreementTemplate?: string; agreementFile?: string }) => {
    rentalOrganizationSettingsModel.update((model) => {
      if (selectedType.startsWith(NAMED_PREFIX)) {
        const name = selectedType.substring(NAMED_PREFIX.length);
        const entries = model.namedAgreements || [];
        const entry = entries.find((candidate) => candidate.name === name);
        if (entry) {
          Object.assign(entry, patch);
        }
        model.namedAgreements = entries;
        return;
      }
      if (!selectedType) {
        Object.assign(model, patch);
        return;
      }
      const typeAgreements = model.typeAgreements || [];
      let entry = typeAgreements.find((candidate) => candidate.trailerType === selectedType);
      if (!entry) {
        entry = { trailerType: selectedType, agreementTemplate: "", agreementFile: "" };
        typeAgreements.push(entry);
      }
      Object.assign(entry, patch);
      model.typeAgreements = typeAgreements;
    });
  }, [rentalOrganizationSettingsModel, selectedType]);

  const addNamedAgreement = useCallback(() => {
    const name = newAgreementName.trim();
    if (!name) {
      return;
    }
    if (namedAgreements.some((entry) => entry.name === name)) {
      enqueueSnackbar("An agreement with this name already exists", { variant: "error" });
      return;
    }
    rentalOrganizationSettingsModel.update((model) => {
      model.namedAgreements = [...(model.namedAgreements || []), { name, agreementTemplate: "", agreementFile: "" }];
    });
    setNewAgreementOpen(false);
    setSelectedType(NAMED_PREFIX + name);
  }, [newAgreementName, namedAgreements, rentalOrganizationSettingsModel]);

  const removeNamedAgreement = useCallback(() => {
    if (!selectedNamedAgreement) {
      return;
    }
    rentalOrganizationSettingsModel.update((model) => {
      model.namedAgreements = (model.namedAgreements || []).filter((entry) => entry.name !== selectedNamedAgreement.name);
    });
    setSelectedType('');
  }, [selectedNamedAgreement, rentalOrganizationSettingsModel]);

  const [previewLoading, setPreviewLoading] = useState(false);

  const reportPreviewError = async (previewError: any) => {
    if (previewError?.response?.status === 422 && previewError.response.data instanceof Blob) {
      try {
        const messages = JSON.parse(await previewError.response.data.text());
        messages.forEach((message: { message: string }) => enqueueSnackbar(message.message, { variant: "error" }));
        return;
      } catch {
        // fall through to the generic message
      }
    }
    enqueueSnackbar("The preview could not be generated. Please try again.", { variant: "error" });
  };

  const previewAgreement = useCallback(() => {
    setPreviewLoading(true);
    const params: Record<string, string> = {};
    if (selectedType.startsWith(NAMED_PREFIX)) {
      params.agreementName = selectedType.substring(NAMED_PREFIX.length);
    } else if (selectedType) {
      params.trailerType = selectedType;
    }
    axios.post(
      endpoints.rentalOrganizationSettings.agreementPreview,
      rentalOrganizationSettingsModel.model,
      { responseType: "blob", params: Object.keys(params).length ? params : undefined }
    ).then((response) => {
      window.open(URL.createObjectURL(new Blob([response.data], { type: "application/pdf" })));
    }).catch(reportPreviewError).finally(() => {
      setPreviewLoading(false);
    });
  }, [rentalOrganizationSettingsModel, selectedType]);

  const previewInsuranceForm = useCallback(() => {
    setPreviewLoading(true);
    axios.post(
      endpoints.rentalOrganizationSettings.insuranceFormPreview,
      rentalOrganizationSettingsModel.model,
      { responseType: "blob" }
    ).then((response) => {
      window.open(URL.createObjectURL(new Blob([response.data], { type: "application/pdf" })));
    }).catch(reportPreviewError).finally(() => {
      setPreviewLoading(false);
    });
  }, [rentalOrganizationSettingsModel]);

  const [isOpenSaveChanges, setIsOpenSaveChanges] = useState(false);

  const updateAgreements = useCallback(() => {
    rentalOrganizationSettingsModel.save().then((response) => {
      if (response) {
        updateData("rentalOrganizationSettings")(response.data as any)

        enqueueSnackbar("Successfully updated the Documents", { variant: "success" })
        setIsOpenSaveChanges(false);
      }
    })
  }, [rentalOrganizationSettingsModel, updateData]);

  const blankForms = rentalOrganizationSettingsModel.model?.blankForms || [];

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {rentalOrganizationSettingsModel.model && <Box sx={{ display: "flex", flexGrow: 1 }}>

          <Stack direction="column" flexGrow={1} gap={2}>
            <PageHeader title="Documents">
              <Button
                onClick={() => setIsOpenSaveChanges(true)}
                variant="outlined"
                color="info"
                startIcon={<Iconify icon="basil:save-solid" />}
              >
                Save Changes
              </Button>
            </PageHeader>

            <Tabs value={documentTab} onChange={(_event, value) => setDocumentTab(value)}>
              <Tab label="Agreements" value="agreements" />
              <Tab label="Insurance" value="insurance" />
            </Tabs>

            {documentTab === "agreements" && <>
              <Card sx={{ overflow: "visible" }}>
                <Stack direction="row" alignItems="center" sx={{ px: 2, pt: 1 }}>
                  <Tabs
                    value={selectedType}
                    onChange={(_event, value) => setSelectedType(value)}
                    variant="scrollable"
                    sx={{ flexGrow: 1 }}
                  >
                    <Tab label="Default" value="" />
                    {RENTAL_TRAILER_TYPES.map((trailerType) => (
                      <Tab key={trailerType.value} label={trailerType.label} value={trailerType.value} />
                    ))}
                    {namedAgreements.map((named) => (
                      <Tab key={named.name} label={named.name} value={NAMED_PREFIX + named.name} />
                    ))}
                  </Tabs>
                  <Button
                    size="small"
                    startIcon={<Iconify icon="mingcute:add-line" />}
                    onClick={() => {
                      setNewAgreementName('');
                      setNewAgreementOpen(true);
                    }}
                  >
                    Add Agreement
                  </Button>
                  {selectedNamedAgreement && (
                    <IconButton size="small" color="error" onClick={removeNamedAgreement}>
                      <Iconify icon="solar:trash-bin-trash-bold" />
                    </IconButton>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1, display: "block" }}>
                  {agreementTabCaption(selectedType, !!selectedNamedAgreement)}
                </Typography>

                <CardHeader title="Agreement Template" sx={{ pt: 1 }} />

                <Stack spacing={2} sx={{ p: 2 }}>
                  <AgreementTemplateEditor
                    key={selectedType}
                    value={agreementTemplate}
                    onChange={(value) => updateAgreement({ agreementTemplate: value })}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Your rental agreement terms, printed at the top of every generated contract.
                    Use Insert Variable to fill in customer, trailer and lease details automatically.
                    Use Insert Choice for &quot;Choose One&quot; selections. You pick the option when sending.
                    Bold and italic apply to whole paragraphs in the PDF.
                  </Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={previewAgreement}
                      disabled={previewLoading}
                      startIcon={<Iconify icon="solar:eye-bold" />}
                    >
                      Preview PDF
                    </Button>
                  </Box>
                </Stack>
              </Card>

              <Card>
                <CardHeader title="Uploaded Agreement" />

                <Stack spacing={2} sx={{ p: 2 }}>
                  <TruxFormField label="Rental Agreement (PDF)">
                    <Stack>
                      <RentalFileField
                        key={selectedType}
                        pdfOnly
                        fileUrl={agreementFile}
                        onChange={(fileUrl) => updateAgreement({ agreementFile: fileUrl })}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Your own rental agreement, placed in front of the generated agreement when it is sent for signing
                      </Typography>
                    </Stack>
                  </TruxFormField>
                </Stack>
              </Card>
            </>}

            {documentTab === "insurance" && <>
              <Card sx={{ overflow: "visible" }}>
                <CardHeader title="Insurance Request Form" />
                <Stack spacing={2} sx={{ p: 2 }}>
                  <AgreementTemplateEditor
                    key="insurance-form"
                    value={rentalOrganizationSettingsModel.model.insuranceFormTemplate || ""}
                    onChange={(value) => rentalOrganizationSettingsModel.update((model) => {
                      model.insuranceFormTemplate = value;
                    })}
                  />
                  <Typography variant="caption" color="text.secondary">
                    The insurance request the customer receives. Use variables to place the trailer,
                    its value and the customer details where you want them.
                  </Typography>
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={previewInsuranceForm}
                      disabled={previewLoading}
                      startIcon={<Iconify icon="solar:eye-bold" />}
                    >
                      Preview PDF
                    </Button>
                  </Box>
                </Stack>
              </Card>

              <Card>
                <CardHeader
                  title="Blank Forms"
                  subheader="PDFs the customer must fill in. They are attached to every insurance request and can be added or removed before sending."
                />
                <Stack spacing={1.5} sx={{ p: 2 }}>
                  {blankForms.map((blankForm, index) => (
                    <Stack key={index} direction="row" gap={2} alignItems="center">
                      <Box flex={1}>
                        <TruxSimpleTextField
                          label="Form Name"
                          value={blankForm.name}
                          onChange={(name) => rentalOrganizationSettingsModel.update((model) => {
                            model.blankForms = (model.blankForms || []).map((entry, i) => i === index ? { ...entry, name } : entry);
                          })}
                        />
                      </Box>
                      <Box flex={1}>
                        <RentalFileField
                          pdfOnly
                          fileUrl={blankForm.fileUrl}
                          onChange={(fileUrl) => rentalOrganizationSettingsModel.update((model) => {
                            model.blankForms = (model.blankForms || []).map((entry, i) => i === index ? { ...entry, fileUrl } : entry);
                          })}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => rentalOrganizationSettingsModel.update((model) => {
                          model.blankForms = (model.blankForms || []).filter((_entry, i) => i !== index);
                        })}
                      >
                        <Iconify icon="solar:trash-bin-trash-bold" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Box>
                    <Button
                      size="small"
                      startIcon={<Iconify icon="mingcute:add-line" />}
                      onClick={() => rentalOrganizationSettingsModel.update((model) => {
                        model.blankForms = [...(model.blankForms || []), { name: "", fileUrl: "" }];
                      })}
                    >
                      Add Blank Form
                    </Button>
                  </Box>
                </Stack>
              </Card>
            </>}
          </Stack>
        </Box>}
      </DisplayPageData >

      {
        rentalOrganizationSettingsModel.model &&
        <SimpleDialog
          title="Save Documents"
          open={isOpenSaveChanges}
          onCancel={() => {
            setIsOpenSaveChanges(false);
            rentalOrganizationSettingsModel.clearErrors();
          }}
          onSubmit={updateAgreements}
          submitDisabled={rentalOrganizationSettingsModel.loading}
          submitButton="Save Documents"
          submitButtonIcon={<Iconify icon="basil:save-solid" />}
          submitButtonColor="info">

          <DisplayErrors savingData={rentalOrganizationSettingsModel} />

          Are you sure you would like to save your changes?
        </SimpleDialog>
      }

      <SimpleDialog
        title="Add Agreement"
        open={newAgreementOpen}
        onCancel={() => setNewAgreementOpen(false)}
        onSubmit={addNamedAgreement}
        submitButton="Add Agreement"
        submitButtonIcon={<Iconify icon="mingcute:add-line" />}
        submitButtonColor="success">
        <TruxSimpleTextField
          label="Agreement Name"
          value={newAgreementName}
          onChange={setNewAgreementName}
        />
      </SimpleDialog>
    </Container >
  );
}
